import { MaskedLine, FadeUp, SectionLabel } from "@/components/Reveal";
import Mark3D from "@/components/Mark3D";

const MARQUEE = [
    "Architecture",
    "Construction",
    "Planning",
    "Interiors",
    "Renovation",
    "Consulting",
];

export default function Intro() {
    return (
        <section
            id="studio"
            data-testid="studio-section"
            className="relative overflow-clip py-32 sm:py-44"
        >
            <div className="blueprint-grid pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-25 [mask-image:linear-gradient(to_left,black,transparent)]" />

            <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
                <FadeUp className="mb-24">
                    <div
                        data-testid="leadership-section"
                        className="grid items-center gap-12 rounded-2xl border border-white/[0.07] bg-onyx p-8 sm:p-12 lg:grid-cols-12"
                    >
                        <div className="lg:col-span-4">
                            <div className="group relative mx-auto max-w-sm">
                                <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-gold/40" />
                                <div className="relative overflow-hidden rounded-2xl border border-white/[0.08]">
                                    <img
                                        src="/images/ceo.jpg"
                                        alt="Ar. Abhishek Aji — ate.teen studio"
                                        loading="lazy"
                                        draggable="false"
                                        onContextMenu={(e) => e.preventDefault()}
                                        className="aspect-[4/5] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-coal/70 via-transparent to-transparent" />
                                    <div className="absolute bottom-5 left-5">
                                        <p className="font-display text-xl font-bold text-white">
                                            Ar. Abhishek Aji
                                        </p>
                                        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                                            CEO &amp; Founder
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-8">
                            <p className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.45em] text-ash">
                                <span className="h-px w-10 bg-gold" />
                                Leadership
                            </p>
                            <blockquote className="mt-8 font-display text-2xl font-medium leading-snug tracking-tight text-white/90 sm:text-3xl">
                                <span className="text-gold">&ldquo;</span>
                                We don&apos;t impose architecture on a site,{" "}
                                <span className="text-gold">
                                    we uncover the architecture that already
                                    belongs there.
                                </span>
                                <span className="text-gold">&rdquo;</span>
                            </blockquote>
                            <p className="mt-8 max-w-xl text-sm leading-relaxed text-ash">
                                Ate.Teen studio is an architecture practice
                                focused on contemporary residential design.
                                Working across architecture, interiors and
                                construction, the studio creates spaces
                                defined by natural light, material honesty
                                and careful attention to detail. Each project
                                responds uniquely to its environment,
                                resulting in architecture that is both
                                contextual and quietly expressive.
                            </p>
                        </div>
                    </div>
                </FadeUp>

                <FadeUp className="mb-24">
                    <div
                        data-testid="brand-mark-section"
                        className="grid items-center gap-10 overflow-hidden rounded-2xl border border-white/[0.07] bg-onyx p-8 sm:p-12 lg:grid-cols-12"
                    >
                        <div className="min-w-0 lg:col-span-5">
                            <p className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.45em] text-ash">
                                <span className="h-px w-10 bg-gold" />
                                The Meaning of Our Brand
                            </p>
                            <h3 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Why the name
                                <br />
                                <span className="text-gold">ate.teen?</span>
                            </h3>
                            <div className="mt-5 max-w-md space-y-4 text-sm leading-relaxed text-ash">
                                <p>
                                    People often ask why I named my
                                    architecture studio Ate.Teen.
                                </p>
                                <p>
                                    The story begins with 18, a number
                                    that&apos;s always been lucky for me.
                                    Instead of using the number directly, I
                                    wanted to express it in a more unique
                                    way, so I wrote it as{" "}
                                    <span className="text-white">Ate.Teen</span>.
                                </p>
                                <p>
                                    Over time, the name took on its own
                                    meaning — it felt like someone doing
                                    something meaningful in their teens.
                                    Coincidentally, I also started my
                                    architecture journey at the age of 18,
                                    making the name even more personal.
                                </p>
                                <p>
                                    What began as a creative twist on a lucky
                                    number has become a reminder of where it
                                    all started — and the passion that
                                    continues to shape every project at
                                    Ate.Teen Studio.
                                </p>
                            </div>
                        </div>
                        <div className="h-[340px] min-w-0 overflow-hidden sm:h-[420px] lg:col-span-7">
                            <Mark3D />
                        </div>
                    </div>
                </FadeUp>

                <SectionLabel index="01" title="The Studio" />

                <h2 className="mt-12 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
                    <MaskedLine delay={0.05}>Architecture is the</MaskedLine>
                    <MaskedLine delay={0.15}>
                        <span className="text-outline">slowest art form —</span>
                    </MaskedLine>
                    <MaskedLine delay={0.25}>
                        we make it <span className="text-gold">timeless.</span>
                    </MaskedLine>
                </h2>

                <FadeUp delay={0.35} className="mt-12 max-w-xl">
                    <p className="text-base leading-relaxed text-ash">
                        Founded in 2023 in Kollam, we operate at the
                        intersection
                        of architecture and engineering, delivering private
                        villas,
                        commercial landmarks and interiors for clients who
                        measure
                        value in decades, not seasons.
                    </p>
                </FadeUp>
            </div>

            <div className="mt-32 overflow-hidden border-y border-white/[0.06] py-8">
                <div
                    className="animate-marquee flex w-max whitespace-nowrap"
                    aria-hidden="true"
                >
                    {[0, 1].map((copy) => (
                        <div key={copy} className="flex items-center">
                            {MARQUEE.map((word) => (
                                <span
                                    key={`${copy}-${word}`}
                                    className="flex items-center"
                                >
                                    <span className="px-8 font-display text-[11vw] font-black uppercase leading-none tracking-tight text-outline sm:text-[7vw]">
                                        {word}
                                    </span>
                                    <span className="h-3 w-3 rotate-45 border border-gold/50" />
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
